/**
 * VRBuildQualityProfile — throttles rendering quality while the user is
 * actively building in VR (build menu open or placement tool engaged),
 * and restores full quality the moment building stops.
 *
 * Driven by worldStore.vrBuildActive (set by VREditorMenu). Distinct from
 * vrBuildMode, which is on for the whole VR session and gates fog/particles.
 *
 * Levers (verified against Spark v2.0.0-preview source):
 * 1. Splat sort throttle — SparkRenderer.autoUpdate is checked once per frame
 *    before kicking off splat accumulation/sorting. Gating it to every Nth
 *    frame cuts the dominant per-frame cost of large splat scenes. While
 *    building the player is mostly stationary, so a stale sort is barely
 *    visible.
 * 2. Spherical harmonics — mesh.maxSh = 0 (flat color) while building.
 *    Runtime-safe: maxSh is a mutable field applied via updateGenerator().
 * 3. Headset fixed foveated rendering — gl.xr.setFoveation(1) renders
 *    peripheral pixels at reduced resolution. Invisible while the user is
 *    focused on an object in front of them.
 *
 * Spark's own foveation params (coneFoveate/behindFoveate) are NOT used —
 * they only affect LoD splats and all meshes currently load with lod: false.
 *
 * Remote avatar animation throttling lives in FixedRetargetedAvatar (it owns
 * the mixers).
 */
import { useEffect, useRef, useCallback } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import { SplatMesh } from '@sparkjsdev/spark';
import { useWorldStore } from '../../stores/worldStore';
import { getSparkInstance } from '../../plugins/marble-worlds/SplatRenderer';

const SORT_EVERY_N_FRAMES = 4; // ~18Hz splat re-sort at 72fps
const SPLAT_RESCAN_MS = 2000; // catch splats that finish loading mid-build

const VRBuildQualityProfile = () => {
  const { scene, gl } = useThree();
  const isPresenting = useXR((s) => !!s.session);
  const vrBuildActive = useWorldStore((s) => s.vrBuildActive);
  const active = isPresenting && vrBuildActive;

  // Original maxSh per mesh so we can restore exactly what each had
  const savedSh = useRef(new Map<SplatMesh, number>());
  const savedFoveation = useRef<number | undefined>(undefined);
  const frame = useRef(0);
  const lastScan = useRef(0);

  const downgradeSplats = useCallback(() => {
    scene.traverse((obj) => {
      if (!(obj instanceof SplatMesh)) return;
      if (savedSh.current.has(obj)) return;
      savedSh.current.set(obj, obj.maxSh);
      if (obj.maxSh !== 0) {
        obj.maxSh = 0;
        obj.updateGenerator();
      }
    });
  }, [scene]);

  useEffect(() => {
    if (!active) return;

    try {
      savedFoveation.current = gl.xr.getFoveation();
      gl.xr.setFoveation(1);
    } catch (e) {
      savedFoveation.current = undefined;
    }

    downgradeSplats();
    console.log(
      `[VRBuildQuality] Throttled — SH0, sort every ${SORT_EVERY_N_FRAMES} frames, max FFR`
    );

    return () => {
      try {
        if (savedFoveation.current !== undefined) {
          gl.xr.setFoveation(savedFoveation.current);
        }
      } catch (e) {}

      savedSh.current.forEach((sh, mesh) => {
        try {
          if (mesh.maxSh !== sh) {
            mesh.maxSh = sh;
            mesh.updateGenerator();
          }
        } catch (e) {} // mesh may have been disposed mid-build
      });
      savedSh.current.clear();

      const spark = getSparkInstance();
      if (spark) spark.autoUpdate = true;

      console.log('[VRBuildQuality] Restored full quality');
    };
  }, [active, gl, downgradeSplats]);

  useFrame(() => {
    if (!active) return;

    // Sort throttle: SparkRenderer only starts a new sort on frames where
    // autoUpdate is true at render time.
    const spark = getSparkInstance();
    if (spark) {
      frame.current = (frame.current + 1) % SORT_EVERY_N_FRAMES;
      spark.autoUpdate = frame.current === 0;
    }

    // Periodically catch splats that loaded after build mode activated
    const now = performance.now();
    if (now - lastScan.current > SPLAT_RESCAN_MS) {
      lastScan.current = now;
      downgradeSplats();
    }
  });

  return null;
};

export default VRBuildQualityProfile;
